import React, { useState } from 'react';
import { Clock, Users, Edit2, Trash2, ToggleLeft, ToggleRight, Sparkles, Lock, Check, AlertCircle, Calendar, Star, MessageSquare, Heart } from 'lucide-react';

const RecipeCard = ({
    recipe,
    onEdit,
    onDelete,
    onTogglePublish,
    onPlan,
    onClick,
    hideStatus,
    // New props for customization
    hidePublicBadge = false,
    hideStatusLabel = false,
    planButtonPosition = 'footer', // 'overlay-top-left', 'footer'
    durationPosition = 'top-right', // 'top-right', 'top-left'
    variant = 'vertical', // 'vertical', 'horizontal'
    showRating = false,
    onToggleFavorite,
    isFavorited = false,
}) => {
    // Derived state from props
    const isPublished = !!recipe.publicationDate;

    const handlePublishClick = (e) => {
        e.stopPropagation();
        // Allow unpublishing if rejected
        if (isPublished && recipe.editorialStatus !== 'rejected') return;
        if (onTogglePublish) onTogglePublish(recipe.id);
    };

    const heroImage = recipe.media?.find(m => m.isHeroShot)?.url || recipe.media?.[0]?.url || "https://pixabay.com/illustrations/paper-old-vintage-compass-retro-8775689/";

    const isHorizontal = variant === 'horizontal';

    return (
        <div
            className={`card border-0 shadow-sm rounded-4 overflow-hidden hover-shadow-md transition-all group cursor-pointer ${isHorizontal ? 'd-flex flex-row h-auto' : 'h-100'}`}
            style={isHorizontal ? { minHeight: '160px' } : {}}
            onClick={onClick}
        >
            {/* Image Header */}
            <div className={`position-relative ${isHorizontal ? 'flex-shrink-0' : ''}`} style={isHorizontal ? { width: '40%' } : { height: '200px' }}>
                <img
                    src={heroImage}
                    alt={recipe.title}
                    className="w-100 h-100 object-fit-cover transition-transform group-hover-scale"
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://placehold.co/600x400?text=No+Image";
                    }}
                />



                {/* Plan Button Overlay - TOP LEFT */}
                {onPlan && planButtonPosition === 'overlay-top-left' && (
                    <div className="position-absolute top-0 start-0 m-2 z-2">
                        <button
                            className="btn btn-light btn-sm rounded-circle p-2 shadow-sm d-flex align-items-center justify-content-center hover-scale text-primary"
                            onClick={(e) => { e.stopPropagation(); onPlan(); }}
                            title="Einplanen"
                            style={{ width: '36px', height: '36px' }}
                        >
                            <Calendar size={18} />
                        </button>
                    </div>
                )}

                {/* Plan Button Overlay - TOP RIGHT */}
                {onPlan && planButtonPosition === 'overlay-top-right' && (
                    <div className="position-absolute top-0 end-0 m-2 z-2">
                        <button
                            className="btn btn-light btn-sm rounded-circle p-2 shadow-sm d-flex align-items-center justify-content-center hover-scale text-primary"
                            onClick={(e) => { e.stopPropagation(); onPlan(); }}
                            title="Einplanen"
                            style={{ width: '36px', height: '36px' }}
                        >
                            <Calendar size={18} />
                        </button>
                    </div>
                )}

                {/* Plan Button Overlay - OLD POS (if not overlay-top-left) implies hidden status mode usually has it top-left or handled elsewhere? 
                    Actually previous logic was: hideStatus && onPlan -> showing a pill button top left.
                    Let's support that if position is not specified or default? 
                    User requested "move 'Einplanen' badge as an overlay to the top left corner".
                    If planButtonPosition === 'overlay-top-left', we show the icon button above.
                */}


                <div className={`position-absolute p-2 ${durationPosition === 'top-left' ? 'top-0 start-0' : 'top-0 end-0'}`}
                    style={{
                        ...(durationPosition === 'top-left' && planButtonPosition === 'overlay-top-left' ? { top: '45px' } : {}),
                        ...(durationPosition === 'top-right' && planButtonPosition === 'overlay-top-right' ? { top: '45px' } : {})
                    }}
                >
                    <div className="bg-white bg-opacity-75 backdrop-blur rounded-pill px-2 py-1 d-flex align-items-center gap-1 shadow-sm">
                        <Clock size={14} className="text-muted" />
                        <span className="small fw-bold text-dark">{recipe.prepTime + recipe.cookTime}m</span>
                    </div>
                </div>

                {!isHorizontal && (
                    <div className="position-absolute bottom-0 start-0 w-100 p-3 bg-gradient-to-t">
                        <h5 className="text-white fw-bold mb-0 text-shadow">{recipe.title}</h5>
                    </div>
                )}
            </div>

            {/* Body */}
            <div className={`card-body ${isHorizontal ? 'd-flex flex-column justify-content-center py-2 px-3' : ''}`}>
                {isHorizontal && (
                    <h6 className="fw-bold mb-2 text-dark">{recipe.title}</h6>
                )}

                <p className={`card-text text-muted small line-clamp-2 ${isHorizontal ? 'mb-2' : ''}`} style={isHorizontal ? { fontSize: '0.85rem' } : { minHeight: '40px' }}>
                    {recipe.description || "Keine Beschreibung verfügbar."}
                </p>

                {/* Tags */}
                {recipe.tags && recipe.tags.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mb-2 mt-2">
                        {recipe.tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="badge bg-light text-dark border fw-normal" style={{ fontSize: '0.7rem' }}>
                                {typeof tag === 'string' ? tag : tag.name}
                            </span>
                        ))}
                        {recipe.tags.length > 3 && (
                            <span className="badge bg-light text-muted border fw-normal" style={{ fontSize: '0.7rem' }}>
                                +{recipe.tags.length - 3}
                            </span>
                        )}
                    </div>
                )}

                <div className={`d-flex align-items-center justify-content-between ${isHorizontal ? 'mt-auto' : 'mt-3 pt-3 border-top border-light'}`}>
                    {/* Rating Display */}
                    {showRating && recipe.feedbackCount > 0 ? (
                        <div className="d-flex align-items-center gap-1" title={`${recipe.avgRating.toFixed(1)} Sterne, ${recipe.feedbackCount} Bewertungen`}>
                            <Star size={14} className="fill-warning text-warning" fill="currentColor" />
                            <span className="small fw-bold text-dark">{recipe.avgRating.toFixed(1)}</span>
                            <span className="small text-muted">({recipe.feedbackCount})</span>
                        </div>
                    ) : null}

                    {/* Publish Action */}
                    {!showRating && !hideStatus && !hideStatusLabel ? (
                        <div
                            className={`d-flex align-items-center gap-2 ${isPublished && recipe.editorialStatus !== 'rejected' ? 'cursor-default' : 'cursor-pointer'}`}
                            onClick={handlePublishClick}
                            title={isPublished ? (recipe.editorialStatus === 'rejected' ? `Rejected: ${recipe.editorialComment}` : "Submitted") : "Jetzt veröffentlichen"}
                        >
                            {(() => {
                                if (!isPublished) return <ToggleLeft size={24} className="text-muted opacity-50 hover-text-primary" />;
                                if (recipe.editorialStatus === 'approved') return <Lock size={20} className="text-success" />;
                                if (recipe.editorialStatus === 'rejected') return <AlertCircle size={20} className="text-danger" />;
                                return <Clock size={20} className="text-warning" />;
                            })()}

                            <span className={`extra-small fw-bold text-uppercase ${!isPublished ? "text-muted" :
                                recipe.editorialStatus === 'approved' ? "text-success" :
                                    recipe.editorialStatus === 'rejected' ? "text-danger" : "text-warning"
                                }`}>
                                {!isPublished ? "Draft" : (recipe.editorialStatus === 'approved' ? "Published" : (recipe.editorialStatus === 'rejected' ? "Rejected" : (recipe.editorialStatus === 'pending' ? "SUBMITTED" : "In Review")))}
                            </span>
                        </div>
                    ) : <div />}

                    {/* Actions */}
                    <div className="d-flex gap-2 align-items-center">
                        {/* Favorite Heart */}
                        {onToggleFavorite && (
                            <button
                                className={`btn btn-sm rounded-circle p-2 d-flex align-items-center justify-content-center transition-all ${isFavorited ? 'text-danger' : 'btn-light text-muted hover-text-danger'}`}
                                onClick={(e) => { e.stopPropagation(); onToggleFavorite(recipe.id); }}
                                title={isFavorited ? "Favorit entfernen" : "Als Favorit merken"}
                                style={{ width: '32px', height: '32px', border: 'none', background: isFavorited ? 'rgba(220,53,69,0.1)' : undefined }}
                            >
                                <Heart size={16} fill={isFavorited ? "currentColor" : "none"} />
                            </button>
                        )}

                        {/* Plan Button - Footer (Default) */}
                        {!hideStatus && planButtonPosition === 'footer' && (
                            <button
                                className="btn btn-primary btn-sm rounded-circle p-2 shadow-sm d-flex align-items-center justify-content-center hover-scale"
                                onClick={(e) => { e.stopPropagation(); if (onPlan) onPlan(); }}
                                title="Einplanen"
                                style={{ width: '32px', height: '32px' }}
                            >
                                <Calendar size={16} />
                            </button>
                        )}

                        {/* If hidden status but not using overlay plan button, maybe we still want it in footer?
                            The requirement for 'Alle Rezepte' was to move it to overlay.
                            So logic handles overlay case. If explicitly footer, show here.
                        */}

                        {!isPublished && (
                            <>
                                <button
                                    className="btn btn-light btn-sm rounded-circle p-2 text-primary hover-bg-primary-subtle"
                                    onClick={(e) => { e.stopPropagation(); onEdit(recipe); }}
                                    title="Bearbeiten"
                                >
                                    <Edit2 size={16} />
                                </button>
                                <button
                                    className="btn btn-light btn-sm rounded-circle p-2 text-danger hover-bg-danger-subtle"
                                    onClick={(e) => { e.stopPropagation(); onDelete(recipe.id); }}
                                    title="Löschen"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
        .bg-gradient-to-t { background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%); }
        .text-shadow { text-shadow: 0 1px 3px rgba(0,0,0,0.8); }
        .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }
        .hover-shadow-md:hover { box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important; transform: translateY(-2px); }
        .group-hover-scale:hover { transform: scale(1.05); }
        .transition-transform { transition: transform 0.3s ease; }
        .cursor-pointer { cursor: pointer; }
      `}</style>
        </div>
    );
};

export default RecipeCard;
